"use client";

import { WorkplaceRequestTypeField } from "@gettactic/api";
import { ImageThumbnail } from "@gettactic/components/src/data-display/ImageThumbnail";
import { HookFormCalendarInput } from "@gettactic/components/src/form/FormCalendarInput";
import { HookFormImageOptionPicker } from "@gettactic/components/src/form/ImageOptionPicker";
import { HookFormTextEditorInput } from "@gettactic/components/src/form/TextEditorInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormTimeSelect } from "@gettactic/components/src/form/TimeSelect";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import {
  HookFormAsyncUserSelectInput,
  HookFormUserSelectInput
} from "@gettactic/components/src/form/UserSelectInput";
import { reservationFieldConfig } from "@gettactic/helpers/src/workplace-requests/reservationField";
import React from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { InfoBlockCard } from "@/features/workplace-requests/components/InfoBlockCard";
import {
  FileUploadFieldInput,
  type GenerateUploadUrl
} from "./FileUploadFieldInput";
import { isImageOptionField } from "./image-option-field";
import { ReservationFieldInput } from "./ReservationFieldInput";
import type { ReservationResourceContext } from "./ReservationResourceAvailability";
import { SeatFieldInput } from "./SeatFieldInput";

interface DynamicFieldInputProps {
  form: UseFormReturn<FieldValues>;
  field: WorkplaceRequestTypeField;
  /**
   * Whether `person` fields may search the org directory. Guest contexts have
   * no access to it, so they render an empty picker instead.
   */
  canSearchUsers?: boolean;
  /**
   * Whether `field` was read off the request's *type*, settings and all.
   *
   * Guest contexts have no type to read and build their fields from the
   * request's field values instead, which carry no `config`. A `reservation`
   * question would then look like one collecting a single day and no more,
   * understating an answer the guest can see in full on the request itself.
   */
  hasFieldSettings?: boolean;
  /** Override the upload endpoint for `file_upload` fields (e.g. guest sharing). */
  generateUpload?: GenerateUploadUrl;
  /** The resource a `reservation` field would book, so it can show its day. */
  reservationResource?: ReservationResourceContext | null;
}

export const DynamicFieldInput: React.FC<DynamicFieldInputProps> = ({
  form,
  field,
  canSearchUsers = true,
  hasFieldSettings = true,
  generateUpload,
  reservationResource = null
}) => {
  const name = field.id;
  const label = field.name;
  const isRequired = field.is_required;
  const help = field.description || undefined;
  const fieldOptions = field.options ?? [];
  const options = fieldOptions.map((o) => ({
    id: o.id,
    name: o.label,
    description: o.description,
    icon: o.image_url ? (
      <ImageThumbnail src={o.image_url} size="xs" />
    ) : undefined
  }));

  const isImagePicker = isImageOptionField(field);
  const imageOptions = fieldOptions.map((o) => ({
    id: o.id,
    label: o.label,
    description: o.description,
    imageUrl: o.image_url
  }));

  switch (field.field_type) {
    // Content, not a control: the field's `name` is an internal constant and is
    // never shown, and nothing here is written back to the ticket.
    case "info_block":
      return <InfoBlockCard html={field.description} />;
    // Rich text, shown back through the basic tag allow-list — so the editor
    // offers no more than that, and Enter breaks the line rather than opening
    // a paragraph, the way a note is written. Answers given before this was
    // rich text are plain strings, and load with their line breaks kept.
    case "long_text":
      return (
        <HookFormTextEditorInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          description={help}
          formatting="basic"
          enterInsertsLineBreak
        />
      );
    case "single_select":
      return isImagePicker ? (
        <HookFormImageOptionPicker
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          description={help}
          options={imageOptions}
        />
      ) : (
        <HookFormUnifiedSelectInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          items={options}
          placeholder="Select an option..."
        />
      );
    case "multi_select":
      return isImagePicker ? (
        <HookFormImageOptionPicker
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          description={help}
          multiselect
          options={imageOptions}
        />
      ) : (
        <HookFormUnifiedSelectInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          multiselect
          items={options}
          placeholder="Select options..."
        />
      );
    case "toggle":
      return <HookFormToggleInput form={form} name={name} label={label} />;
    case "date":
      return (
        <HookFormCalendarInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          // A single date needs no Apply step: picking the day is the answer.
          setValueOnApply={false}
        />
      );
    case "time":
      return (
        <HookFormTimeSelect
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          description={help}
          placeholder="Select time"
        />
      );
    case "person":
      return canSearchUsers ? (
        <HookFormAsyncUserSelectInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          placeholder="Choose a person..."
        />
      ) : (
        <HookFormUserSelectInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          users={[]}
          placeholder="Choose a person..."
        />
      );
    case "number":
      return (
        <HookFormTextInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          type="number"
          placeholder={help}
        />
      );
    case "reservation":
      return (
        <ReservationFieldInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          description={help}
          config={hasFieldSettings ? reservationFieldConfig(field) : undefined}
          canSearchUsers={canSearchUsers}
          resource={reservationResource}
        />
      );
    case "seat":
      return (
        <SeatFieldInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          description={help}
        />
      );
    case "file_upload":
      return (
        <FileUploadFieldInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          description={help}
          generateUpload={generateUpload}
        />
      );
    default:
      return (
        <HookFormTextInput
          form={form}
          name={name}
          label={label}
          isRequired={isRequired}
          placeholder={help}
        />
      );
  }
};
